"use client";

import { AuthedImage } from "@/components/authed-image";
import type { HomeworkPhotoDto, HomeworkTaskDto } from "@/lib/types";
import { cn } from "@/lib/utils";

function photoStamp(photo: HomeworkPhotoDto) {
  return photo.createdAt || "";
}

export function uniqueHomeworkPhotos(tasks: HomeworkTaskDto[]): HomeworkPhotoDto[] {
  const byId = new Map<string, HomeworkPhotoDto>();
  for (const task of tasks) {
    for (const photo of task.photos) {
      const existing = byId.get(photo.id);
      const stamp = photoStamp(photo);
      if (!existing) {
        byId.set(photo.id, photo);
        continue;
      }
      const previous = photoStamp(existing);
      if (stamp && (!previous || stamp < previous)) byId.set(photo.id, photo);
    }
  }
  return [...byId.values()].sort((left, right) => {
    const newer = photoStamp(right).localeCompare(photoStamp(left));
    return newer !== 0 ? newer : left.id.localeCompare(right.id);
  });
}

export function DiaryPhotoBubbles({
  photos,
  onOpenPhoto,
  className,
}: {
  photos: HomeworkPhotoDto[];
  onOpenPhoto: (url: string, alt: string) => void;
  className?: string;
}) {
  if (photos.length === 0) return null;
  return (
    <div className={cn("flex flex-wrap gap-1.5", className)}>
      {photos.map((photo) => (
        <button
          key={photo.id}
          type="button"
          className="size-10 shrink-0 overflow-hidden rounded-xl bg-accent ring-1 ring-foreground/10"
          aria-label="З дошки"
          onClick={(event) => {
            event.stopPropagation();
            onOpenPhoto(photo.url, "Фото з дошки");
          }}
        >
          <AuthedImage src={photo.url} alt="" className="size-10 object-cover" />
        </button>
      ))}
    </div>
  );
}

export function DiaryTaskLine({
  task,
  onOpenPhoto,
  onEdit,
  showPhoto = true,
}: {
  task: HomeworkTaskDto;
  onOpenPhoto: (url: string, alt: string) => void;
  onEdit?: () => void;
  showPhoto?: boolean;
}) {
  const photo = showPhoto ? task.photos[0] : undefined;
  const body = (
    <div className="flex min-w-0 flex-1 items-start gap-2">
      <p className="min-w-0 flex-1 text-sm leading-5">{task.task}</p>
      {photo ? (
        <button
          type="button"
          className="size-10 shrink-0 overflow-hidden rounded-xl bg-accent ring-1 ring-foreground/10"
          aria-label="З дошки"
          onClick={(event) => {
            event.stopPropagation();
            onOpenPhoto(photo.url, "Фото з дошки");
          }}
        >
          <AuthedImage src={photo.url} alt="" className="size-10 object-cover" />
        </button>
      ) : null}
    </div>
  );

  if (!onEdit) return body;

  return (
    <button type="button" className="flex w-full min-w-0 text-left" onClick={onEdit}>
      {body}
    </button>
  );
}

export function DiaryTaskList({
  tasks,
  onOpenPhoto,
  onEdit,
  className,
  showPhoto = true,
}: {
  tasks: HomeworkTaskDto[];
  onOpenPhoto: (url: string, alt: string) => void;
  onEdit?: (task: HomeworkTaskDto) => void;
  className?: string;
  showPhoto?: boolean;
}) {
  if (tasks.length === 0) return null;
  return (
    <div className={cn("space-y-2", className)}>
      {tasks.map((task) => (
        <DiaryTaskLine
          key={task.id}
          task={task}
          showPhoto={showPhoto}
          onOpenPhoto={onOpenPhoto}
          onEdit={onEdit ? () => onEdit(task) : undefined}
        />
      ))}
    </div>
  );
}
